import { useMemo, useState } from "react"
import type { TileDisplay, WidgetType } from "@csbi/contracts"
import { catalog, type MetricDefinition } from "@csbi/metric-catalog"
import { TileWidget } from "@csbi/widgets"
import type { ThemeMode } from "@csbi/viz-core"
import { useFilterStore } from "../../filters/model/filter-store.js"
import { useTileQuery, type TileSpec } from "../hooks/use-tile-query.js"
import { drillableDim } from "../drill.js"
import { useUserNames } from "../use-user-names.js"
import { DrillDrawer } from "./drill-drawer.js"

const DEFINITIONS: ReadonlyMap<string, MetricDefinition> = new Map(catalog.map((m) => [m.id, m]))

export type TileProps = {
  spec: TileSpec
  title?: string
  widget?: WidgetType
  display?: Partial<TileDisplay>
  mode: ThemeMode
  height?: number | string
  hero?: boolean
  actions?: boolean
}

export function Tile({ spec, title, widget, display, mode, height, hero, actions }: TileProps) {
  const filters = useFilterStore((s) => s.filters)
  const query = useTileQuery(spec, filters)

  const drillDim = actions === false ? null : drillableDim(spec)
  const [drillCategory, setDrillCategory] = useState<string | null>(null)

  const showsUsers = (spec.splitBy ?? []).includes("user")
  const users = useUserNames(showsUsers)

  const first = DEFINITIONS.get(spec.metrics[0] ?? "")
  const resolvedWidget: WidgetType = widget ?? first?.defaultWidget ?? "bar"
  const resolvedTitle = useMemo(
    () => title ?? spec.metrics.map((id) => DEFINITIONS.get(id)?.name ?? id).join(" · "),
    [title, spec.metrics],
  )

  return (
    <>
      <TileWidget
        title={resolvedTitle}
        widget={resolvedWidget}
        {...(query.data ? { result: query.data } : {})}
        {...(display ? { display } : {})}
        definitions={DEFINITIONS}
        mode={mode}
        {...(height ? { height } : {})}
        {...(query.isLoading ? { loading: true } : {})}
        {...(query.dimmed ? { dimmed: true } : {})}
        {...(query.errorMessage ? { error: query.errorMessage } : {})}
        {...(hero ? { hero } : {})}
        {...(actions === false ? { actions: false } : {})}
        onRefresh={() => void query.refetchFresh()}
        {...(query.isFetching ? { refreshing: true } : {})}
        {...(drillDim ? { onDrill: ({ category }) => category && setDrillCategory(category) } : {})}
        {...(users.label ? { categoryLabel: users.label } : {})}
      />
      {drillDim && (
        <DrillDrawer
          spec={spec}
          dim={drillDim}
          category={drillCategory}
          chartTitle={resolvedTitle}
          mode={mode}
          {...(users.label ? { categoryLabel: users.label } : {})}
          {...(drillDim === "user" ? { user: drillCategory ? users.lookup(drillCategory) : undefined } : {})}
          onClose={() => setDrillCategory(null)}
        />
      )}
    </>
  )
}

export { DEFINITIONS as METRIC_DEFINITIONS }
